/**
 * Suggestion Service
 *
 * Handles CRUD operations for sale suggestions (US-013).
 * Suggestions are predicted orders that users can confirm, edit, or dismiss.
 */

import { supabase } from '../supabase-client'
import { toLocalDateString } from '../utils/dateUtils'

// =============================================================================
// FETCH OPERATIONS
// =============================================================================

/**
 * Fetch pending suggestions for display in the sales timeline
 *
 * @param {Object} options - Filter options
 * @param {string} [options.customerId] - Filter by specific customer
 * @param {string} [options.branchId] - Filter by specific branch
 * @param {Date} [options.fromDate] - Filter suggestions from this date
 * @param {Date} [options.toDate] - Filter suggestions up to this date
 * @param {number} [options.minConfidence] - Minimum confidence score (0-1)
 * @param {number} [options.limit] - Maximum number of suggestions to return
 * @returns {Promise<Array>} Array of suggestions with customer/branch/product details
 */
export async function fetchSuggestions(options = {}) {
  const {
    customerId,
    branchId,
    fromDate,
    toDate,
    minConfidence = 0,
    limit = 50
  } = options

  let query = supabase
    .from('sale_suggestions')
    .select(`
      *,
      customer:customers(id, name),
      branch:branches(id, name, address),
      product:products(id, strain)
    `)
    .eq('status', 'pending')
    .gte('confidence_score', minConfidence)
    .order('suggested_date', { ascending: true })
    .order('confidence_score', { ascending: false })
    .limit(limit)

  if (customerId) {
    query = query.eq('customer_id', customerId)
  }

  if (branchId) {
    query = query.eq('branch_id', branchId)
  }

  if (fromDate) {
    query = query.gte('suggested_date', toLocalDateString(fromDate))
  }

  if (toDate) {
    query = query.lte('suggested_date', toLocalDateString(toDate))
  }

  const { data, error } = await query

  if (error) {
    console.error('[SuggestionService] Error fetching suggestions:', error)
    throw error
  }

  return data || []
}

/**
 * Fetch a single suggestion by ID
 *
 * @param {string} suggestionId - UUID of the suggestion
 * @returns {Promise<Object>} Suggestion with related data
 */
export async function fetchSuggestionById(suggestionId) {
  const { data, error } = await supabase
    .from('sale_suggestions')
    .select(`
      *,
      customer:customers(id, name),
      branch:branches(id, name, address),
      product:products(id, strain)
    `)
    .eq('id', suggestionId)
    .single()

  if (error) {
    console.error('[SuggestionService] Error fetching suggestion:', error)
    throw error
  }

  return data
}

// =============================================================================
// CONFIRMATION & CONVERSION
// =============================================================================

/**
 * Confirm a suggestion and convert it to a sale
 *
 * This function:
 * 1. Creates a new sale with the suggestion data
 * 2. Updates the suggestion status to 'converted'
 * 3. Records feedback for algorithm training
 *
 * @param {string} suggestionId - UUID of the suggestion to confirm
 * @param {Object} [edits] - Optional edits to the suggestion
 * @param {Date} [edits.date] - Override the suggested date
 * @param {number} [edits.quantity] - Override the suggested quantity
 * @param {number} [edits.price] - Override the suggested price
 * @param {string} [edits.consignmentNo] - Consignment number for the sale
 * @returns {Promise<Object>} The created sale
 */
export async function confirmSuggestion(suggestionId, edits = {}) {
  // Fetch the suggestion first
  const suggestion = await fetchSuggestionById(suggestionId)

  if (!suggestion) {
    throw new Error('Suggestion not found')
  }

  if (suggestion.status !== 'pending') {
    throw new Error(`Cannot confirm suggestion with status: ${suggestion.status}`)
  }

  // Determine final values (use edits or original suggestion)
  const finalDate = edits.date || suggestion.suggested_date
  const finalQuantity = edits.quantity ?? suggestion.suggested_quantity
  const finalPrice = edits.price ?? suggestion.suggested_price

  // Create the sale
  const { data: sale, error: saleError } = await supabase
    .from('sales')
    .insert([{
      date: finalDate,
      customer_id: suggestion.customer_id,
      branch_id: suggestion.branch_id,
      consignment_no: edits.consignmentNo || null,
      status: 'pending'
    }])
    .select()
    .single()

  if (saleError) {
    console.error('[SuggestionService] Error creating sale:', saleError)
    throw saleError
  }

  // Create the sale item
  // Note: We need product_type_id but suggestions don't store it
  // The UI will need to provide this or we fetch from a default
  const { error: itemError } = await supabase
    .from('sale_items')
    .insert([{
      sale_id: sale.id,
      product_id: suggestion.product_id,
      product_type_id: edits.productTypeId || null, // Must be provided by caller
      quantity: finalQuantity,
      price: finalPrice,
      packed: false
    }])

  if (itemError) {
    console.error('[SuggestionService] Error creating sale item:', itemError)
    // Rollback the sale if item creation fails
    await supabase.from('sales').delete().eq('id', sale.id)
    throw itemError
  }

  // Update suggestion status
  const { error: updateError } = await supabase
    .from('sale_suggestions')
    .update({
      status: 'converted',
      converted_sale_id: sale.id,
      converted_at: new Date().toISOString()
    })
    .eq('id', suggestionId)

  if (updateError) {
    console.error('[SuggestionService] Error updating suggestion status:', updateError)
    // Note: Sale was created successfully, just logging the status update failure
  }

  // Record feedback for algorithm training
  const hasEdits = edits.date || edits.quantity !== undefined
  await recordFeedback({
    suggestionId,
    suggestion,
    action: hasEdits ? 'edited' : 'confirmed',
    editedDate: edits.date || null,
    editedQuantity: edits.quantity ?? null
  })

  return sale
}

// =============================================================================
// DISMISSAL
// =============================================================================

/**
 * Dismiss a suggestion (user doesn't want this order)
 *
 * @param {string} suggestionId - UUID of the suggestion to dismiss
 * @param {string} [reason] - Optional reason for dismissal (for training)
 * @returns {Promise<void>}
 */
export async function dismissSuggestion(suggestionId, reason = null) {
  // Fetch the suggestion first for feedback recording
  const suggestion = await fetchSuggestionById(suggestionId)

  if (!suggestion) {
    throw new Error('Suggestion not found')
  }

  if (suggestion.status !== 'pending') {
    throw new Error(`Cannot dismiss suggestion with status: ${suggestion.status}`)
  }

  // Update suggestion status
  const { error } = await supabase
    .from('sale_suggestions')
    .update({ status: 'dismissed' })
    .eq('id', suggestionId)

  if (error) {
    console.error('[SuggestionService] Error dismissing suggestion:', error)
    throw error
  }

  // Record feedback for algorithm training
  await recordFeedback({
    suggestionId,
    suggestion,
    action: 'dismissed',
    dismissalReason: reason
  })
}

// =============================================================================
// FEEDBACK RECORDING (INTERNAL)
// =============================================================================

/**
 * Record feedback for algorithm training
 * Internal function - called by confirm/dismiss operations
 *
 * @param {Object} params - Feedback parameters
 * @param {string} params.suggestionId - UUID of the suggestion
 * @param {Object} params.suggestion - The suggestion object
 * @param {string} params.action - Action taken: 'confirmed', 'dismissed', 'edited', 'expired'
 * @param {Date} [params.editedDate] - User-corrected date (if edited)
 * @param {number} [params.editedQuantity] - User-corrected quantity (if edited)
 * @param {string} [params.dismissalReason] - Reason for dismissal (if dismissed)
 * @returns {Promise<void>}
 */
async function recordFeedback({
  suggestionId,
  suggestion,
  action,
  editedDate = null,
  editedQuantity = null,
  dismissalReason = null
}) {
  // Get current user ID
  const { data: { user } } = await supabase.auth.getUser()

  const { error } = await supabase
    .from('prediction_feedback')
    .insert([{
      suggestion_id: suggestionId,
      customer_id: suggestion.customer_id,
      branch_id: suggestion.branch_id,
      product_id: suggestion.product_id,
      original_date: suggestion.suggested_date,
      original_quantity: suggestion.suggested_quantity,
      original_confidence: suggestion.confidence_score,
      action,
      edited_date: editedDate,
      edited_quantity: editedQuantity,
      dismissal_reason: dismissalReason,
      user_id: user?.id || null
    }])

  if (error) {
    // Log but don't throw - feedback recording should not block the main operation
    console.error('[SuggestionService] Error recording feedback:', error)
  }
}

// =============================================================================
// SUGGESTION CREATION (for testing/seeding)
// =============================================================================

/**
 * Create a new suggestion
 * Primarily used for testing and seeding data
 *
 * @param {Object} suggestionData - Suggestion details
 * @param {string} suggestionData.customerId - Customer UUID
 * @param {string} [suggestionData.branchId] - Branch UUID (optional)
 * @param {string} suggestionData.productId - Product UUID
 * @param {string} suggestionData.suggestedDate - Date in YYYY-MM-DD format
 * @param {number} suggestionData.suggestedQuantity - Quantity in kg
 * @param {number} [suggestionData.suggestedPrice] - Price per kg
 * @param {number} [suggestionData.confidenceScore] - Confidence 0-1 (default 0.5)
 * @param {Object} [suggestionData.predictionBasis] - Algorithm metadata
 * @returns {Promise<Object>} Created suggestion
 */
export async function createSuggestion(suggestionData) {
  const {
    customerId,
    branchId = null,
    productId,
    suggestedDate,
    suggestedQuantity,
    suggestedPrice = null,
    confidenceScore = 0.5,
    predictionBasis = {}
  } = suggestionData

  const { data, error } = await supabase
    .from('sale_suggestions')
    .insert([{
      customer_id: customerId,
      branch_id: branchId,
      product_id: productId,
      suggested_date: suggestedDate,
      suggested_quantity: suggestedQuantity,
      suggested_price: suggestedPrice,
      confidence_score: confidenceScore,
      prediction_basis: predictionBasis
    }])
    .select()
    .single()

  if (error) {
    console.error('[SuggestionService] Error creating suggestion:', error)
    throw error
  }

  return data
}

// =============================================================================
// BULK OPERATIONS
// =============================================================================

/**
 * Create multiple suggestions at once
 * Used by the suggestion generation algorithm
 *
 * @param {Array<Object>} suggestions - Array of suggestion data
 * @returns {Promise<Array>} Created suggestions
 */
export async function createSuggestionsBatch(suggestions) {
  const insertData = suggestions.map(s => ({
    customer_id: s.customerId,
    branch_id: s.branchId || null,
    product_id: s.productId,
    suggested_date: s.suggestedDate,
    suggested_quantity: s.suggestedQuantity,
    suggested_price: s.suggestedPrice || null,
    confidence_score: s.confidenceScore || 0.5,
    prediction_basis: s.predictionBasis || {}
  }))

  const { data, error } = await supabase
    .from('sale_suggestions')
    .insert(insertData)
    .select()

  if (error) {
    console.error('[SuggestionService] Error creating suggestions batch:', error)
    throw error
  }

  return data || []
}

/**
 * Expire old suggestions
 * Marks pending suggestions as expired if past their expiry date or suggested date
 *
 * @returns {Promise<number>} Count of expired suggestions
 */
export async function expireOldSuggestions() {
  // Call the database function
  const { data, error } = await supabase.rpc('expire_old_suggestions')

  if (error) {
    console.error('[SuggestionService] Error expiring suggestions:', error)
    throw error
  }

  return data || 0
}

// =============================================================================
// SUPPLY CONSTRAINTS
// =============================================================================

/**
 * Fetch active supply constraints
 *
 * @param {string} [productId] - Optional filter by product
 * @returns {Promise<Array>} Active constraints
 */
export async function fetchSupplyConstraints(productId = null) {
  const today = toLocalDateString(new Date())

  let query = supabase
    .from('supply_constraints')
    .select(`
      *,
      product:products(id, strain)
    `)
    .lte('effective_from', today)
    .or(`effective_until.is.null,effective_until.gte.${today}`)

  if (productId) {
    query = query.eq('product_id', productId)
  }

  const { data, error } = await query

  if (error) {
    console.error('[SuggestionService] Error fetching supply constraints:', error)
    throw error
  }

  return data || []
}

/**
 * Create a supply constraint
 *
 * @param {Object} constraintData - Constraint details
 * @returns {Promise<Object>} Created constraint
 */
export async function createSupplyConstraint(constraintData) {
  const { data: { user } } = await supabase.auth.getUser()

  const { data, error } = await supabase
    .from('supply_constraints')
    .insert([{
      product_id: constraintData.productId,
      constraint_type: constraintData.constraintType,
      available_quantity: constraintData.availableQuantity,
      effective_from: constraintData.effectiveFrom || toLocalDateString(new Date()),
      effective_until: constraintData.effectiveUntil || null,
      notes: constraintData.notes || null,
      created_by: user?.id || null
    }])
    .select()
    .single()

  if (error) {
    console.error('[SuggestionService] Error creating supply constraint:', error)
    throw error
  }

  return data
}

// =============================================================================
// FORECASTING AGGREGATIONS (for Reports Dashboard)
// =============================================================================

/**
 * Get demand forecast aggregated by date and product
 * Used by the Forecasting tab in Reports Dashboard
 *
 * @returns {Promise<Object>} Aggregated forecast data
 */
export async function getDemandForecast() {
  const { data, error } = await supabase
    .from('sale_suggestions')
    .select(`
      suggested_date,
      suggested_quantity,
      confidence_score,
      prediction_basis,
      product:products(id, strain),
      branch:branches(
        id,
        name,
        type:customer_types(id, name)
      )
    `)
    .eq('status', 'pending')
    .order('suggested_date', { ascending: true })

  if (error) {
    console.error('[SuggestionService] Error fetching demand forecast:', error)
    throw error
  }

  // Aggregate by date
  const byDate = {}
  const byProduct = {}
  const byCustomerType = {}
  const confidenceDistribution = { high: 0, medium: 0, low: 0 }

  for (const suggestion of data || []) {
    const date = suggestion.suggested_date
    const strain = suggestion.product?.strain || 'Unknown'
    const customerType = suggestion.branch?.type?.name || 'Unknown'
    const qty = suggestion.suggested_quantity || 0
    const confidence = suggestion.confidence_score || 0

    // By date
    if (!byDate[date]) {
      byDate[date] = { date, totalQuantity: 0, suggestions: 0, avgConfidence: 0 }
    }
    byDate[date].totalQuantity += qty
    byDate[date].suggestions += 1
    byDate[date].avgConfidence += confidence

    // By product
    if (!byProduct[strain]) {
      byProduct[strain] = { strain, totalQuantity: 0, suggestions: 0 }
    }
    byProduct[strain].totalQuantity += qty
    byProduct[strain].suggestions += 1

    // By customer type
    if (!byCustomerType[customerType]) {
      byCustomerType[customerType] = { type: customerType, totalQuantity: 0, suggestions: 0 }
    }
    byCustomerType[customerType].totalQuantity += qty
    byCustomerType[customerType].suggestions += 1

    // Confidence distribution
    if (confidence >= 0.7) {
      confidenceDistribution.high += 1
    } else if (confidence >= 0.4) {
      confidenceDistribution.medium += 1
    } else {
      confidenceDistribution.low += 1
    }
  }

  // Calculate average confidence per date
  Object.values(byDate).forEach(d => {
    d.avgConfidence = d.suggestions > 0 ? d.avgConfidence / d.suggestions : 0
  })

  // Count unique deliveries (customer+date combinations)
  const uniqueDeliveries = new Set()
  for (const suggestion of data || []) {
    uniqueDeliveries.add(`${suggestion.branch?.id || 'unknown'}-${suggestion.suggested_date}`)
  }

  return {
    byDate: Object.values(byDate),
    byProduct: Object.values(byProduct).sort((a, b) => b.totalQuantity - a.totalQuantity),
    byCustomerType: Object.values(byCustomerType).sort((a, b) => b.totalQuantity - a.totalQuantity),
    confidenceDistribution,
    totalSuggestions: data?.length || 0,
    totalDeliveries: uniqueDeliveries.size,
    totalForecastQuantity: Object.values(byDate).reduce((sum, d) => sum + d.totalQuantity, 0)
  }
}

/**
 * Get trend analysis from suggestion prediction_basis metadata
 * Extracts moving average data for trend visualization
 *
 * @returns {Promise<Object>} Trend analysis data
 */
export async function getTrendAnalysis() {
  const { data, error } = await supabase
    .from('sale_suggestions')
    .select(`
      prediction_basis,
      confidence_score,
      product:products(id, strain),
      branch:branches(
        id,
        name,
        type:customer_types(id, name)
      )
    `)
    .eq('status', 'pending')
    .not('prediction_basis', 'is', null)

  if (error) {
    console.error('[SuggestionService] Error fetching trend analysis:', error)
    throw error
  }

  const trendsByType = {}
  const anomalies = []

  for (const suggestion of data || []) {
    const basis = suggestion.prediction_basis || {}
    const customerType = suggestion.branch?.type?.name || 'Unknown'
    const strain = suggestion.product?.strain || 'Unknown'

    // Extract moving average data if present
    if (basis.shortTermMA !== undefined || basis.mediumTermMA !== undefined) {
      if (!trendsByType[customerType]) {
        trendsByType[customerType] = {
          type: customerType,
          shortTermMAs: [],
          mediumTermMAs: [],
          weights: basis.typeWeights || { shortTermWeight: 0.5, mediumTermWeight: 0.5 }
        }
      }
      if (basis.shortTermMA) trendsByType[customerType].shortTermMAs.push(basis.shortTermMA)
      if (basis.mediumTermMA) trendsByType[customerType].mediumTermMAs.push(basis.mediumTermMA)

      // Track anomalies
      if (basis.trendAnomaly) {
        anomalies.push({
          strain,
          customerType,
          shortTermMA: basis.shortTermMA,
          mediumTermMA: basis.mediumTermMA,
          deviation: basis.shortTermMA && basis.mediumTermMA
            ? Math.abs(basis.shortTermMA - basis.mediumTermMA) / basis.mediumTermMA
            : 0
        })
      }
    }
  }

  // Calculate averages for each type
  Object.values(trendsByType).forEach(type => {
    type.avgShortTermMA = type.shortTermMAs.length > 0
      ? type.shortTermMAs.reduce((a, b) => a + b, 0) / type.shortTermMAs.length
      : 0
    type.avgMediumTermMA = type.mediumTermMAs.length > 0
      ? type.mediumTermMAs.reduce((a, b) => a + b, 0) / type.mediumTermMAs.length
      : 0
    // Clean up arrays to reduce payload size
    delete type.shortTermMAs
    delete type.mediumTermMAs
  })

  return {
    trendsByType: Object.values(trendsByType),
    anomalies: anomalies.slice(0, 10), // Top 10 anomalies
    totalWithTrendData: data?.length || 0
  }
}
